/* eslint-disable ember/no-incorrect-calls-with-inline-anonymous-functions, ember/no-runloop */
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed, set } from '@ember/object';
import type Owner from '@ember/owner';
import { trackedArray } from '@ember/reactive/collections';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didUpdate } from '@ember/render-modifiers';
import type Transition from '@ember/routing/transition';
import { scheduleOnce } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBadge,
  PrismCard,
  PrismPerfectScrollbar,
} from '@mylighthouse/prism-ember';
import { range } from '@nullvoxpopuli/ember-composable-helpers';
import { task } from 'ember-concurrency';
// @ts-expect-error: Incorrect type
import DraggableObjectTarget from 'ember-drag-drop/components/draggable-object-target';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import { and, not } from 'ember-truth-helpers';
import type { Roomtype } from 'frontend-utils/models';
import type Ota from 'frontend-utils/models/ota';
import type { RoomtypeMappingType } from 'frontend-utils/models/roomtype';
import {
  OTA_IDS,
  PRODUCT_NAMES,
} from 'frontend-utils/utils/constants/constant-values';
import HotelAndMarketDropdownsSelectionOtas from 'lumen/components/hotel-and-market/dropdowns/selection-otas';
import SettingsCentralMappingFormFieldsAddRoomtype from 'lumen/components/settings/central-mapping/form-fields/add-roomtype';
import SettingsCentralMappingRoomtypeMappingCellHeaderRoomtype from 'lumen/components/settings/central-mapping/roomtype-mapping/cell-header-roomtype';
import SettingsCentralMappingRoomtypeMappingCellRoomtype from 'lumen/components/settings/central-mapping/roomtype-mapping/cell-roomtype';
import { ROOMTYPE_MAPPING_INFLUENCED_PRODUCTS_TRANSLATIONS } from 'lumen/utils/central-mapping-constants';
import { STANDARD_ROOMS } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import { sortRoomtypeIds } from 'lumen/utils/sort-ids';
import { timeout } from 'lumen/utils/waiters';
import type PerfectScrollbar from 'perfect-scrollbar';

interface SettingsCentralMappingRoomtypeMappingSignature {
  Args: {
    canSelectOTA: boolean;
    chooseOTA: (ota: Ota) => void;
    dataType: string;
    doRefresh: () => void;
    isRoomtypeRequestLoading: boolean | undefined;
    otaId: string;
    roomtypes: Roomtype[] | null | undefined;
    selectedOTA: Ota | null;
  };
}

export interface Room {
  isCustom?: boolean;
  isPersisted?: boolean;
  name: string;
  proper_name: string;
  rid: string;
}

export default class SettingsCentralMappingRoomtypeMapping extends Component<SettingsCentralMappingRoomtypeMappingSignature> {
  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarket: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked addedRooms = trackedArray<Room>();
  @tracked canTriggerScrollToEnd = false;
  @tracked hasSkeletonBeenShown = false;
  @tracked isMobile = false;
  @tracked isRoomtypeUpdateLoading = false;
  @tracked newRooms = trackedArray<string>();

  draggedHotelInfoId: string | null = null;
  previousTarget: HTMLElement | null = null;

  showSkeletonOnlyOnce = modifier((_element, [isLoading]): void => {
    if (isLoading) {
      return;
    }
    this.hasSkeletonBeenShown = true;
  });

  transitionHandler: ((transition: Transition) => Promise<void>) | null = null;

  addActiveClassTask = task(
    { restartable: true },
    async (content: { target: HTMLElement }): Promise<void> => {
      await timeout('extra-short');
      if (this.previousTarget) {
        this.previousTarget.classList.remove('active');
      }
      let { target } = content;
      while (target.tagName !== 'TD') {
        target = target.parentNode as HTMLElement;
      }
      target.classList.add('active');
      this.previousTarget = target;
    },
  );

  editRoomtypeNameTask = task({}, async (roomType: Room, newName: string) => {
    set(roomType, 'proper_name', newName);

    const isSameName = roomType.name === roomType.proper_name;
    if (isSameName) {
      return;
    }

    // Revert the room name without displaying an alert when the user enters an empty string.
    if (roomType.proper_name === '') {
      set(roomType, 'proper_name', roomType.name);
      return;
    }

    const isStandardRoomtypeName = STANDARD_ROOMS.includes(
      roomType.proper_name.toLowerCase(),
    );

    if (isStandardRoomtypeName) {
      set(roomType, 'proper_name', roomType.name);

      return await this.alerting.showErrorAlert({
        text: this.intl.t('rates.roomtype.alert_predefined_room_type'),
      });
    }

    const nameExists = this.allRoomSelections.some(
      (roomSelection) =>
        roomSelection.rid !== roomType.rid &&
        roomSelection.proper_name.toString().toLowerCase() ===
          roomType.proper_name.toLowerCase(),
    );

    if (nameExists) {
      set(roomType, 'proper_name', roomType.name);

      return await this.alerting.showErrorAlert({
        title: this.intl.t('rates.roomtype.error'),
        text: this.intl.t('rates.roomtype.alert_used_name'),
      });
    }

    if (!roomType.isPersisted) {
      set(roomType, 'name', roomType.proper_name);

      const roomIndex = this.addedRooms.findIndex(
        (room) => room.rid === roomType.rid,
      );

      if (roomIndex === -1) {
        return;
      }

      this.addedRooms[roomIndex] = {
        proper_name: roomType.proper_name,
        name: roomType.name,
        // Prevent users from being able to end up with duplicate rids when renaming
        // E.g: create roomtype 'Emmelie', rename to 'Emmelie rename'. If you create a new roomtype 'Emmelie' again, both would have rid of 'c_Emmelie'
        rid: `c_${roomType.proper_name.replace(/ /g, '_')}`,
      };

      return;
    }

    // Don't call API for added rooms that haven't been posted yet
    try {
      const editUrl = `${this.ajax.host}/api/v3/hotel/roomtypenamechange/${this.hotelAndMarket?.hotel?.id}/`;
      await this.ajax.request(editUrl, {
        method: 'POST',
        dataType: 'json',
        contentType: 'application/json; charset=utf-8',
        data: JSON.stringify({
          roomtype_id: roomType.rid,
          roomtype_name: roomType.proper_name,
        }),
      });

      set(roomType, 'name', roomType.proper_name);

      const roomIndex = this.addedRooms.findIndex(
        (room) => room.rid === roomType.rid,
      );

      if (roomIndex === -1) {
        return;
      }

      const currentRoomId = this.addedRooms[roomIndex]!.rid;

      this.addedRooms[roomIndex] = {
        proper_name: roomType.proper_name,
        name: roomType.name,
        // Don't overwrite rid here. It's persisted, so the API has already generated a c_{number} id
        rid: currentRoomId,
      };
    } catch (error) {
      // Revert the proper_name to the previous version
      set(roomType, 'proper_name', roomType.name);

      await this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });
    }
  });

  scrollOnDragTask = task(
    { drop: true },
    async (tbody: HTMLElement, scrollToLeft: boolean): Promise<void> => {
      let delta = 40;
      if (scrollToLeft) {
        delta *= -1;
      }

      (tbody as HTMLDivElement).scrollBy(delta, 0);
      await timeout('extra-short');
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get addedRoomtypes() {
    const addedRoomtypes: Room[] = [];
    this.addedRooms.forEach((item) => {
      if (!item?.rid) {
        return;
      }

      const isCustom = item.rid.startsWith('c_');

      addedRoomtypes.push({
        proper_name: item.proper_name,
        name: item.name,
        rid: item.rid,
        isCustom,
        isPersisted: item.isPersisted,
      });
    });

    return addedRoomtypes;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get allRoomSelections() {
    return this.availableRoomtypes.concat(this.addedRoomtypes);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get autocompleteDefaultOptions() {
    const usedRoomIds = this.allRoomSelections.map((roomtype) => roomtype.rid);
    // first filter the default roomtypes that are already present in the mapping
    // then create the options with the roomtypes that are left
    return STANDARD_ROOMS.filter(
      (roomId) => usedRoomIds.indexOf(roomId) === -1,
    ).map((roomId) => {
      return {
        rid: roomId,
        name: roomId,
        proper_name: this.intl.t(`rates.roomtypes.${roomId}`).toString(),
      };
    });
  }

  /*
   * Return all unique room types (headers) already added to the mapping
   * The name is used to display the room, the id is used as a class in the HTML
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get availableRoomtypes() {
    const availableRoomtypes: Room[] = [];
    const checkRoomTypes: string[] = []; // No duplicates

    this.args.roomtypes?.forEach((roomtype) => {
      roomtype.roomtypeSelections.forEach((selection) => {
        const roomname = selection.name;
        const roomid = selection.id;

        // First check to see if it doesn't exist already
        if (checkRoomTypes.includes(roomid)) {
          return;
        }

        const isCustom = roomid.startsWith('c_');

        availableRoomtypes.push({
          proper_name: isCustom
            ? roomname
            : this.intl.t(`rates.roomtypes.${roomname}`),
          name: roomname,
          isPersisted: true,
          rid: roomid,
          isCustom,
        });

        checkRoomTypes.push(roomid);
      });
    });

    return availableRoomtypes;
  }

  @computed(
    'args.{otaId,dataType}',
    'hotelAndMarket.allUniqueAndOrderedCompetitors.[]',
    'hotelAndMarket.hotel.{hotelinfo,id}',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotels() {
    let hotels = [this.hotelAndMarket.hotel?.hotelinfo];

    // Only add competitors for Rates and Pricing Manager. Not for Parity.
    if (this.args.dataType !== 'parity') {
      hotels = hotels.concat(
        this.hotelAndMarket.allUniqueAndOrderedCompetitors.filter((hotelinfo) =>
          this.hotelAndMarket.isHotelinfoMatched(hotelinfo, this.args.otaId),
        ),
      );
    }

    return hotels;
  }

  @computed(
    'access.{featureFlags,hasParity,hasPricingAssistantTab,hasRates}',
    'args.otaId',
    'hotelAndMarket.hotel.{paRatePreference.channel,OTAsParity.[],OTAsRates.[]}',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get influencedProducts() {
    const products = new Set<string>();
    const { otaId } = this.args;
    const isAllChannels = otaId === OTA_IDS.ALL_CHANNELS;
    const { hasPricingAssistantTab, hasRates, hasParity } = this.access;
    const {
      OTAsRates = [],
      OTAsParity = [],
      paRatePreference,
    } = this.hotelAndMarket?.hotel || {};

    const shouldIncludePricingManager =
      hasPricingAssistantTab &&
      (isAllChannels || otaId === paRatePreference?.channel);

    const shouldIncludeRateInsight =
      hasRates && (isAllChannels || OTAsRates.some((ota) => ota.id === otaId));

    const shouldIncludeParityInsight =
      hasParity &&
      (isAllChannels || OTAsParity.some((ota) => ota.id === otaId));

    if (shouldIncludePricingManager) {
      products.add(
        ROOMTYPE_MAPPING_INFLUENCED_PRODUCTS_TRANSLATIONS[
          PRODUCT_NAMES.PRODUCT_PRICING_MANAGER
        ],
      );
    }

    if (shouldIncludeRateInsight) {
      products.add(
        ROOMTYPE_MAPPING_INFLUENCED_PRODUCTS_TRANSLATIONS[
          PRODUCT_NAMES.PRODUCT_RATE_INSIGHT
        ],
      );
    }

    if (shouldIncludeParityInsight) {
      products.add(
        ROOMTYPE_MAPPING_INFLUENCED_PRODUCTS_TRANSLATIONS[
          PRODUCT_NAMES.PRODUCT_PARITY_INSIGHT
        ],
      );
    }

    return products;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoading() {
    return this.args.isRoomtypeRequestLoading || this.isRoomtypeUpdateLoading;
  }

  get sortedSelections(): Room[] {
    /*  Custom sorting
        Defines a custom sorting that puts rooms that were added last are put last
        First are all default roomtypes that are present on initial render of the popup in a fixed order (see STANDARD_ROOMS array)
        Next are all custom roomtypes that are present on initial render, sorted alfabetically by id
        Last are all rooms that were added in the order they were added
    */
    const addedRoomIds = this.addedRooms.map((roomtype) => roomtype?.rid);

    return this.allRoomSelections.sort((a: Room, b: Room) => {
      const aIndex = addedRoomIds.indexOf(a.rid);
      const bIndex = addedRoomIds.indexOf(b.rid);

      if (aIndex !== -1) {
        if (bIndex !== -1) {
          return aIndex > bIndex ? 1 : -1;
        }

        return 1;
      }

      if (bIndex !== -1) {
        return -1;
      }

      return sortRoomtypeIds(a.rid, b.rid);
    });
  }

  constructor(
    owner: Owner,
    args: SettingsCentralMappingRoomtypeMappingSignature['Args'],
  ) {
    super(owner, args);

    this.isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );
  }

  willDestroy(): void {
    super.willDestroy();

    this.previousTarget = null;

    if (this.transitionHandler) {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      this.router.off('routeWillChange', this.transitionHandler);
      this.transitionHandler = null;
    }
  }

  updateScrollPosition(perfectScrollbar?: unknown): void {
    // Enables fixed header scrolling
    const header = document.getElementById('roomtype-mapping-fixed-header');
    const tbody = document.getElementById('roomtype-mapping-table-body');
    if (!tbody) {
      return;
    }
    // Enables fixed column scrolling
    const fixedTableCells = document.querySelectorAll(
      '.fixed-roomtype-mapping-cell',
    );

    if (header) {
      header.style.left = `${-tbody.scrollLeft}px`;
    }

    fixedTableCells.forEach((columnRow) => {
      (columnRow as HTMLElement).style.left = `${tbody.scrollLeft}px`;
    });

    if (perfectScrollbar) {
      (perfectScrollbar as PerfectScrollbar).update();
    }
  }

  @action addColumn(): void {
    this.canTriggerScrollToEnd = true;
    scheduleOnce('afterRender', this, () => {
      const tbody = document.getElementById('roomtype-mapping-table-body');
      if (tbody) {
        if (this.canTriggerScrollToEnd) {
          // Make the table scroll to the right when an input field is added
          tbody.scrollLeft = 6000;
          this.canTriggerScrollToEnd = false;
        }
      }

      this.updateScrollPosition();
    });
  }

  /**
   * We need to clear the addedRooms and newRomms. Otherwise these would persist even after switching OTAs
   * @param { OTA } ota : OTA model object
   */
  @action clearRoomsAndChooseOTA(ota: Ota): void {
    this.hasSkeletonBeenShown = false;
    this.addedRooms = trackedArray();
    this.newRooms = trackedArray();
    this.args.chooseOTA(ota);
  }

  @action closeAddColumn(roomName?: string): void {
    if (roomName) {
      this.addColumn();
    }
  }

  @action dragEndedForHotel(): void {
    document
      .querySelectorAll(`[data-hotel='${this.draggedHotelInfoId}']`)
      .forEach((droptargetCell) => {
        droptargetCell.classList.remove('droppable-target');
        droptargetCell.classList.remove('active');
      });
    this.draggedHotelInfoId = null;
  }

  @action dragStartedForHotel(hotelinfoId: string, roomtypeId: string): void {
    this.draggedHotelInfoId = hotelinfoId;
    document
      .querySelectorAll(
        `[data-hotel='${this.draggedHotelInfoId}']:not([data-roomtype='${roomtypeId}'])`,
      )
      .forEach((droptargetCell) =>
        droptargetCell.classList.add('droppable-target'),
      );
  }

  @action onCloseAddColumn(perfectScrollbar: unknown): void {
    (perfectScrollbar as PerfectScrollbar).update();
  }

  @action onInitPerfectScrollbar(perfectScrollbar: unknown): void {
    this.updateScrollPosition(perfectScrollbar);
  }

  @action
  async onSaveRoomtypeName(room: Room, newName: string): Promise<void> {
    await this.editRoomtypeNameTask.perform(room, newName);
  }

  @action
  async onTableDrag(event: MouseEvent): Promise<void> {
    const tbody = document.getElementById('roomtype-mapping-table-body');
    if (!tbody) {
      return;
    }
    const rect = tbody.getBoundingClientRect();
    const leftThreshold = rect.left + 250; // width of the fixed header
    const rightThreshold = rect.right;
    // The amount of pixels next to the border where the scroll is triggered
    const scrollMargin = 50;

    if (event.clientX < leftThreshold + scrollMargin) {
      await this.scrollOnDragTask.perform(tbody, true); // scroll left
    } else if (event.clientX > rightThreshold - scrollMargin) {
      await this.scrollOnDragTask.perform(tbody, false); // scroll right
    }
  }

  @action onTableScroll(perfectScrollbar: unknown): void {
    this.updateScrollPosition(perfectScrollbar);
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async overrideRoom(
    hotelinfoId: string,
    room: RoomtypeMappingType,
    newRoomtypeId: string,
    newRoomtypeName: string,
  ) {
    // Remove any newRooms that don't have a mapping anymore
    const mapping = this.args.roomtypes?.[0]?.roomtypeMapping ?? [];

    this.newRooms = trackedArray(
      this.newRooms.filter((id) => {
        return mapping.some((room) => room.roomtype_id === id);
      }),
    );

    const isCustomRoomtype = !STANDARD_ROOMS.includes(newRoomtypeId);
    const isNewRoomtype =
      isCustomRoomtype &&
      this.addedRooms.some((room) => room.rid === newRoomtypeId) &&
      !this.newRooms.includes(newRoomtypeId);

    const data: Record<string, string> = {
      roomname: room.name,
      roomtype_name: newRoomtypeName,
    };

    let isCustomNewRoomtypeEmpty = false;
    if (isCustomRoomtype) {
      // Determine if the new roomtype is not already present in the mapping
      isCustomNewRoomtypeEmpty = mapping.every(
        (room) => room.roomtype_id !== newRoomtypeId,
      );
    }

    // For newly created custom roomtypes or already existing empty ones, omit roomtype_id from the payload (BE deletes a custom roomtype without mapping)
    // Otherwise, include roomtype_id.
    const shouldIncludeRoomtypeId = !isNewRoomtype && !isCustomNewRoomtypeEmpty;

    if (shouldIncludeRoomtypeId) {
      data['roomtype_id'] = newRoomtypeId;
    }

    const oldRoomtypeId = room.roomtype_id;
    if (oldRoomtypeId === newRoomtypeId) {
      return this.dragEndedForHotel();
    }

    try {
      const newMapping = await this.ajax.request<{
        roomtype_id: string;
        roomtype_name: string;
      }>(
        `/hotel/roomtypechange/${this.hotelAndMarket?.hotel?.id}/${hotelinfoId}/`,
        {
          method: 'POST',
          data: JSON.stringify(data),
        },
      );

      if (isNewRoomtype) {
        const roomIndex = this.addedRooms.findIndex(
          (room) => room.rid === newRoomtypeId,
        );

        if (roomIndex === -1) {
          return;
        }

        this.addedRooms[roomIndex] = {
          proper_name: newMapping.roomtype_name,
          name: newMapping.roomtype_name,
          rid: newMapping.roomtype_id,
          // Override the isPersisted property of added roomtypes so the API is called when editing the roomtype name
          isPersisted: true,
        };

        // Update newRooms with the newly created roomtype id
        this.newRooms.push(newMapping.roomtype_id);
      }

      set(room, 'roomtype_id', newMapping.roomtype_id);
      set(room, 'overridden', true);
    } catch (failure) {
      this.errorTracking.captureException(failure);
    }

    this.dragEndedForHotel();
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async overrideRoomDrag(
    hotelinfoId: string | undefined,
    content: RoomtypeMappingType,
    ops: { target: { rid: string; type: string } },
  ) {
    const newRoomtypeName = ops.target.type;
    const newRoomtypeId = ops.target.rid;

    if (newRoomtypeName === '') {
      await this.alerting.showErrorAlert({
        title: this.intl.t('rates.roomtype.error').toString(),
        text: this.intl.t('rates.roomtype.alert_no_room').toString(),
      });

      return false;
    }

    // Testing out the new tracking service, eventually one of these ⬆️⬇️ tracking calls will be removed
    this.usageAnalytics.track('OTA Room Type Mapped', {
      eventTrigger: 'room-type-overview-modal',
      otaName: this.args.otaId,
      from: content.roomtype_name,
      to: newRoomtypeName,
    });

    if (hotelinfoId) {
      await this.overrideRoom(
        hotelinfoId,
        content,
        newRoomtypeId,
        newRoomtypeName,
      );
    }

    return true;
  }

  @action
  async reColor(content: { target: HTMLElement }): Promise<void> {
    await this.addActiveClassTask.perform(content);
  }

  @action updateAddedRooms(addedRoom: Room): void {
    this.addedRooms.push(addedRoom);
  }

  <template>
    <PrismCard @contentLayout="noPadding" @isParent={{false}}>
      <:content>
        <div class="roomtype-mapping">
          <div class="align-center flex justify-between">
            {{#if @canSelectOTA}}
              <HotelAndMarketDropdownsSelectionOtas
                @cssClasses="prism-dropdown-trigger"
                @includeAllChannels={{this.access.hasParity}}
                @selectOta={{this.clearRoomsAndChooseOTA}}
                {{! @glint-expect-error: Incorrect type }}
                @selectedOTA={{@selectedOTA}}
                @type="rates"
              />
            {{/if}}
            {{#if this.access.hasRoomtypeMappingEditRights}}
              <div class="align-center flex gap-300">
                <SettingsCentralMappingFormFieldsAddRoomtype
                  @allRoomSelections={{this.allRoomSelections}}
                  @autocompleteDefaultOptions={{this.autocompleteDefaultOptions}}
                  @closeAddColumn={{this.closeAddColumn}}
                  @updateAddedRooms={{this.updateAddedRooms}}
                />
              </div>
            {{/if}}
          </div>
          <div class="flex gap-200">
            <p class="paragraph-300--regular text-neutral-subdued">{{t
                "settings.central_mapping.competitor_rooms.influenced_products"
              }}</p>
            <div class="flex gap-200">
              {{#each this.influencedProducts as |product|}}
                <PrismBadge @label={{t product}} />
              {{/each}}
            </div>
          </div>

          {{#if
            (and @isRoomtypeRequestLoading (not this.hasSkeletonBeenShown))
          }}
            <div
              class="roomtype-mapping-table-container"
              {{this.showSkeletonOnlyOnce @isRoomtypeRequestLoading}}
            >
              <div
                class="roomtype-mapping-fixed-header roomtype-mapping-fixed-header__skeleton"
              >
                <table>
                  <thead class={{if this.isMobile "mobile-header"}}>
                    <tr class="header-row">
                      <th class="empty fixed-roomtype-mapping-cell" />
                      {{#each (range 1 5)}}
                        <th>
                          <div class="flex flex-col gap-200 p-200">
                            <div class="loading-pulse w-1/2"></div>
                          </div>
                        </th>
                      {{/each}}
                    </tr>
                  </thead>
                  <tbody>
                    {{#each (range 1 10)}}
                      <tr>
                        <td class="fixed-roomtype-mapping-cell px-200 pt-300">
                          <div class="loading-pulse w-3/4"></div>
                        </td>
                        {{#each (range 1 5)}}
                          <td class="px-200 pt-300">
                            <div class="loading-pulse w-3/4"></div>
                          </td>
                        {{/each}}
                      </tr>
                    {{/each}}
                  </tbody>
                </table>
              </div>
            </div>
          {{else}}
            <div class="roomtype-mapping-table-container">
              <PrismPerfectScrollbar
                @customContainerId="roomtype-mapping-table-body"
                @customXrailOffset={{250}}
                as |perfectScrollbar|
              >
                <div
                  class="position-relative"
                  id="roomtype-mapping-table-body"
                  {{didUpdate
                    (fn this.onInitPerfectScrollbar perfectScrollbar)
                    perfectScrollbar
                  }}
                  {{didUpdate
                    (fn this.onCloseAddColumn perfectScrollbar)
                    this.addedRooms
                  }}
                  {{on "dragover" this.onTableDrag}}
                  {{on "scroll" (fn this.onTableScroll perfectScrollbar)}}
                  {{! Bit counterintiutive here, but a did-update is needed as did-insert would trigger before the perfect scrollbar is initialized }}
                >
                  <table>
                    <thead
                      class="{{if this.isMobile 'mobile-header'}}
                        roomtype-mapping-fixed-header"
                    >
                      <tr class="header-row">
                        <th class="empty fixed-roomtype-mapping-cell" />
                        {{#each this.sortedSelections as |roomtype|}}
                          <SettingsCentralMappingRoomtypeMappingCellHeaderRoomtype
                            @canEdit={{this.access.hasRoomtypeMappingEditRights}}
                            @onSaveRoomtypeName={{this.onSaveRoomtypeName}}
                            @roomtype={{roomtype}}
                          />
                        {{/each}}
                      </tr>
                    </thead>
                    <tbody>
                      {{#each this.hotels as |hotel|}}
                        <tr>
                          <td class="hotelid fixed-roomtype-mapping-cell">
                            <div class="roomtype-competitor-name">
                              <p class="text-300--regular">{{hotel.name}}</p>
                            </div>
                            <div
                              class="roomtype-competitor-compset-tag-container"
                            >
                              {{#each hotel.compsets as |compset|}}
                                <div>
                                  <span
                                    class="roomtype-competitor-compset-tag text-200--regular"
                                  >
                                    {{t compset.translationKey}}
                                  </span>
                                </div>
                              {{/each}}
                            </div>
                          </td>
                          {{#each this.sortedSelections as |roomtype|}}
                            <td
                              class="drop-target-div"
                              data-hotel={{hotel.id}}
                              data-roomtype={{roomtype.rid}}
                            >
                              <DraggableObjectTarget
                                {{! @glint-expect-error}}
                                @action={{fn this.overrideRoomDrag hotel.id}}
                                @dragOverAction={{this.reColor}}
                                @rid={{roomtype.rid}}
                                @type={{roomtype.name}}
                              >
                                <SettingsCentralMappingRoomtypeMappingCellRoomtype
                                  @canEdit={{this.access.hasRoomtypeMappingEditRights}}
                                  {{! @glint-expect-error: Incorrect type }}
                                  @dataType={{@dataType}}
                                  @dragStartedForHotel={{this.dragStartedForHotel}}
                                  @hotel={{this.hotelAndMarket.hotel}}
                                  @hotelinfo={{hotel}}
                                  @otaId={{@otaId}}
                                  @overrideRoom={{this.overrideRoom}}
                                  @roomtype={{roomtype}}
                                  @sortedSelections={{this.sortedSelections}}
                                />
                              </DraggableObjectTarget>
                            </td>
                          {{/each}}
                        </tr>
                      {{/each}}
                    </tbody>
                  </table>
                </div>
              </PrismPerfectScrollbar>
            </div>
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
